import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import { ArrowDownCircle, ArrowUpCircle, ArrowRightLeft, ClipboardCheck, Activity, Download, Trash2 } from 'lucide-react';
import { parseServerDate } from '../lib/constants';

const TYPE_META = {
  in:            { label: 'Barang Masuk',   icon: ArrowDownCircle, color: 'bg-green-100 text-green-800' },
  out:           { label: 'Barang Keluar',  icon: ArrowUpCircle,   color: 'bg-orange-100 text-orange-800' },
  transfer_in:   { label: 'Transfer Masuk', icon: ArrowDownCircle, color: 'bg-blue-100 text-blue-800' },
  transfer_out:  { label: 'Transfer Keluar',icon: ArrowUpCircle,   color: 'bg-indigo-100 text-indigo-800' },
  opname:        { label: 'Stok Opname',    icon: ClipboardCheck,  color: 'bg-amber-100 text-amber-800' },
};

const StockFlowPage = () => {
  const { user, hasPermission } = useAuth();
  const [movements, setMovements] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterArea, setFilterArea] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const canDelete = user?.role === 'super_admin';
  const canExport = hasPermission('view_warehouse');

  // Area is a wider grouping than lokasi (branch): each location optionally belongs to an area.
  const areaOptions = useMemo(
    () => Array.from(new Set(locations.map((l) => l.area).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [locations]
  );
  const locationAreaMap = useMemo(
    () => locations.reduce((map, l) => { map[l.id] = l.area || null; return map; }, {}),
    [locations]
  );
  const locationsInArea = filterArea === 'all' ? locations : locations.filter((l) => l.area === filterArea);

  const handleAreaFilterChange = (value) => {
    setFilterArea(value);
    if (value !== 'all' && filterLocation !== 'all' && locationAreaMap[filterLocation] !== value) {
      setFilterLocation('all');
    }
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    if (filterType !== 'all') params.set('type', filterType);
    if (filterLocation !== 'all') {
      params.set('location_id', filterLocation);
    } else if (filterArea !== 'all') {
      params.set('location_ids', locationsInArea.map((l) => l.id).join(','));
    }
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params;
  };

  const loadMovements = () => {
    setLoading(true);
    api.get(`/warehouse/movements?${buildParams()}`)
      .then((r) => setMovements(r.data || []))
      .catch(() => setMovements([]))
      .finally(() => setLoading(false));
  };

  const handleDelete = async (mv) => {
    if (!window.confirm(`Hapus catatan arus stok "${mv.item_name}" (${mv.type})? Stok tidak akan otomatis dikembalikan — gunakan opname bila perlu.`)) return;
    try {
      await api.delete(`/warehouse/movements/${mv.id}`);
      toast.success('Arus stok dihapus');
      setMovements((prev) => prev.filter((x) => x.id !== mv.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus arus stok');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/exports/stock-movements?${buildParams()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const fn = res.headers['content-disposition']?.match(/filename=([^;]+)/)?.[1] || 'arus_stok.xlsx';
      link.setAttribute('download', fn);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel selesai');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal export');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get('/locations').then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadMovements();
  }, [filterType, filterArea, filterLocation, dateFrom, dateTo]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return movements;
    return movements.filter((m) => (
      `${m.item_name || ''} ${m.reference || ''} ${m.notes || ''}`.toLowerCase().includes(q)
    ));
  }, [movements, searchQuery]);

  const fmt = (iso) => {
    if (!iso) return '-';
    try { return parseServerDate(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }); }
    catch (_) { return iso; }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="stockflow-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Gudang</p>
            <h1 className="text-4xl font-medium tracking-tight flex items-center gap-3">
              <Activity className="w-8 h-8" /> Arus Stok
            </h1>
            <p className="text-muted-foreground mt-1">Histori semua pergerakan stok: masuk (PR), keluar (tiket), transfer antar cabang, dan opname</p>
          </div>
          {canExport && (
            <Button onClick={handleExport} disabled={exporting || movements.length === 0} variant="outline" size="sm" data-testid="flow-export-button">
              <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Mengunduh...' : 'Export Excel'}
            </Button>
          )}
        </div>

        <Card className="border-border">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Cari</Label>
              <Input placeholder="Barang / ref / catatan..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9" data-testid="flow-search-input" />
            </div>
            <div>
              <Label className="text-xs">Jenis</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="h-9" data-testid="flow-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="in">Barang Masuk</SelectItem>
                  <SelectItem value="out">Barang Keluar</SelectItem>
                  <SelectItem value="transfer_in">Transfer Masuk</SelectItem>
                  <SelectItem value="transfer_out">Transfer Keluar</SelectItem>
                  <SelectItem value="opname">Stok Opname</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {areaOptions.length > 0 && (
              <div>
                <Label className="text-xs">Area</Label>
                <Select value={filterArea} onValueChange={handleAreaFilterChange}>
                  <SelectTrigger className="h-9" data-testid="flow-filter-area"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Area</SelectItem>
                    {areaOptions.map((area) => <SelectItem key={area} value={area}>{area}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Lokasi</Label>
              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger className="h-9" data-testid="flow-filter-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Lokasi</SelectItem>
                  {locationsInArea.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dari</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9" data-testid="flow-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-9" data-testid="flow-filter-to" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-medium">
              {loading ? 'Memuat...' : `${filtered.length} Pergerakan`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {!loading && filtered.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Tidak ada pergerakan stok pada rentang ini</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tanggal</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jenis</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Barang</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Lokasi</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Referensi</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Keterangan</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Oleh</th>
                      {canDelete && <th className="w-12 px-2 py-3"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((m) => {
                      const meta = TYPE_META[m.type] || { label: m.type, color: 'bg-gray-100 text-gray-800' };
                      const Icon = meta.icon;
                      const isOpname = m.type === 'opname';
                      return (
                        <tr key={m.id} className="hover:bg-secondary/30" data-testid={`flow-row-${m.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-xs">{fmt(m.created_at)}</td>
                          <td className="px-4 py-3">
                            <Badge className={`text-[10px] ${meta.color}`}>
                              {Icon && <Icon className="w-3 h-3 mr-1 inline" />}
                              {meta.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium">{m.item_name}</p>
                            <p className="text-[10px] text-muted-foreground capitalize">{m.item_category}</p>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{m.location_id}</td>
                          <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                            {isOpname && m.delta !== undefined && m.delta !== null ? (
                              <span className={m.delta === 0 ? 'text-muted-foreground' : m.delta > 0 ? 'text-green-700' : 'text-red-700'}>
                                {m.delta > 0 ? '+' : ''}{m.delta}
                              </span>
                            ) : (
                              <>{m.quantity}</>
                            )} {m.unit}
                          </td>
                          <td className="px-4 py-3 text-xs">{m.reference || '-'}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px] truncate" title={m.notes || ''}>
                            {m.notes || '-'}
                          </td>
                          <td className="px-4 py-3 text-xs">{m.performed_by_name || '-'}</td>
                          {canDelete && (
                            <td className="px-2 py-3 text-center">
                              <button type="button" onClick={() => handleDelete(m)}
                                className="text-muted-foreground hover:text-destructive"
                                title="Hapus arus stok"
                                data-testid={`flow-delete-${m.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default StockFlowPage;
